import type { Stage as KonvaStage } from "konva/lib/Stage";
import type { Ref } from "react";

import type { PlanogramItem, PlanogramItemOrientation } from "@CommonTypes/merchflow/pog/item";
import type { Pog } from "@CommonTypes/merchflow/pog/pog";
import type {
  PlanogramMoveCapability,
  PlanogramMoveCommit,
  PlanogramMoveResult,
  PlanogramMoveResultEvent,
  PlanogramOperation,
} from "./operations";
import type { OverlayChannelInput } from "./overlays";
import type { ViewportConfig, ViewportState } from "./viewport";

export type PlanogramSnapshot = Pog;

export type PlanogramEntityKind =
  | "bay"
  | "shelf"
  | "product"
  | "pegboard"
  | "placement"
  | "container";

export interface PlanogramEntityRef {
  kind: PlanogramEntityKind;
  id: string;
}

export interface PlanogramContextRequest {
  readonly entity: PlanogramEntityRef;
  readonly selection: PlanogramSelection;
  /** Pointer coordinates are semantic menu placement data, not renderer events. */
  readonly position?: {
    readonly clientX: number;
    readonly clientY: number;
  };
}

export interface PlanogramOverlayBadgeRequest {
  readonly entityId: string;
  readonly position: {
    readonly clientX: number;
    readonly clientY: number;
  };
}

export interface PlanogramSelection {
  kind: PlanogramEntityKind | null;
  ids: readonly string[];
  anchorId: string | null;
  focusedId: string | null;
}

export const emptyPlanogramSelection: PlanogramSelection = {
  anchorId: null,
  focusedId: null,
  ids: [],
  kind: null,
};

export type PlanogramProductDisplayMode = "image" | "label";
/** A product field rendered as a label; kept generic so adapters can preserve domain label options. */
export type PlanogramProductLabel = keyof PlanogramItem | "none";

export interface PlanogramViewConfig {
  height?: number;
  offsetX?: number;
  offsetY?: number;
  productColors?: ReadonlyMap<string, string>;
  productDisplayMode?: PlanogramProductDisplayMode;
  productLabel?: PlanogramProductLabel;
  scale?: number;
  width?: number;
}

export interface PlanogramProductAssetRequest {
  productCode: string;
  productId: string;
}

export interface PlanogramProductAsset {
  image: HTMLImageElement;
  alt?: string;
}

export interface PlanogramAssetProvider {
  loadProductImage: (
    request: PlanogramProductAssetRequest,
  ) => PlanogramProductAsset | null | Promise<PlanogramProductAsset | null>;
}

export interface PlanogramPolicy {
  /** Whether movement and resize affordances should be enabled for the adapter. */
  readonly canMove?: boolean;
  readonly canSelect?: (entity: PlanogramEntityRef) => boolean;
  readonly evaluate: (pog: Pog, operation: PlanogramOperation) => PlanogramMoveCapability;
  readonly execute: (args: {
    readonly operation: PlanogramOperation;
    readonly pog: Pog;
    readonly selection?: PlanogramSelection;
  }) => PlanogramMoveResult;
}

export interface PlanogramWorldRect {
  height: number;
  width: number;
  x: number;
  y: number;
}

export type PlanogramProductCellKind = "main" | "x-cap" | "y-cap";

export interface PlanogramProductCell extends PlanogramWorldRect {
  column: number;
  kind: PlanogramProductCellKind;
  orientation: PlanogramItemOrientation;
  row: number;
  unitsHigh: number;
  unitsWide: number;
}

export interface PlanogramSceneContainer extends PlanogramWorldRect {
  color: string;
  id: string;
  label: string;
  shelfType: "REGULAR" | "HANGCELL" | "BASKET";
}

export interface PlanogramSceneBay extends PlanogramWorldRect {
  baseHeight: number;
  bayNo: number;
  id: string;
  maxNotch: number;
  notchOffset: number;
  notchSpacing: number;
}

export interface PlanogramSceneShelf extends PlanogramWorldRect {
  bayId: string;
  groupId: string;
  id: string;
  merchandisableWidth: number;
  /**
   * Height of the item-placement zone beyond the shelf's own thickness (up to the shelf above,
   * or bay top edge) — excludes the thickness band, which is covered by `height`/`y` instead.
   */
  nonMerchandisableExtensionHeight: number;
  /** Scene y of the extension zone (see nonMerchandisableExtensionHeight). */
  nonMerchandisableExtensionY: number;
  shelfNo: number;
  thickness: number;
}

export type PlanogramFixtureType = "RISER" | "HANGCELL";

export interface PlanogramSceneFixture extends PlanogramWorldRect {
  bayId: string;
  fixtureType: PlanogramFixtureType;
  id: string;
  merchandisableWidth: number;
  /**
   * Height of the item-placement zone beyond the fixture's own thickness: up to the shelf above
   * (or bay top edge) for RISER, down to the shelf below (or bay floor) for HANGCELL. For
   * HANGCELL this equals `height`/`y`, since those already exclude the peg-bar thickness.
   */
  nonMerchandisableExtensionHeight: number;
  /** Scene y of the extension zone (see nonMerchandisableExtensionHeight). */
  nonMerchandisableExtensionY: number;
  shelfNo: number;
  thickness: number;
}

export interface PlanogramSceneProduct extends PlanogramWorldRect {
  cells: PlanogramProductCell[];
  horizontalFacings: number;
  id: string;
  isNewInsertedItem: boolean;
  label: string;
  name: string;
  orientation: PlanogramItemOrientation;
  productCode: string;
  shelfGroupId: string;
  shelfId: string;
  validOrientations: PlanogramItemOrientation[];
  verticalFacings: number;
}

export interface PlanogramSceneHole extends PlanogramWorldRect {
  column: number;
  row: number;
}

export interface PlanogramScenePegboard extends PlanogramWorldRect {
  bayId: string;
  holeGapHorizontal: number;
  holeGapVertical: number;
  holeOffsetHorizontal: number;
  holeOffsetVertical: number;
  holes: PlanogramSceneHole[];
  id: string;
}

export interface PlanogramScenePlacementCell extends PlanogramWorldRect {
  depth: number;
  holeNoLeft: number;
  holeNoTop: number;
}

export interface PlanogramScenePlacement extends PlanogramWorldRect {
  id: string;
  facingChangeColor: string | null;
  facingChangeType: string | null;
  facingCells: PlanogramScenePlacementCell[];
  label: string;
  name: string;
  pegboardId: string;
  productCode: string;
  productId: string;
}

export interface PlanogramScene {
  bays: PlanogramSceneBay[];
  containers: PlanogramSceneContainer[];
  fixtures: PlanogramSceneFixture[];
  height: number;
  pegboards?: PlanogramScenePegboard[];
  placements?: PlanogramScenePlacement[];
  products: PlanogramSceneProduct[];
  shelves: PlanogramSceneShelf[];
  unsupportedFixtureIds: string[];
  width: number;
}

export interface PlanogramRendererLabels {
  readonly bay: (bayNo: number) => string;
  readonly containers: string;
  readonly fixture: (fixtureType: PlanogramFixtureType, shelfNo: number) => string;
  readonly fixtures: string;
  readonly newItemIndicator: string;
  readonly pegboardFacingChangeIndicator: (changeType: string) => string;
  readonly moveLeft: string;
  readonly moveProductLeft: (label: string) => string;
  readonly moveProductRight: (label: string) => string;
  readonly moveRight: string;
  readonly pegboards: string;
  readonly placements: string;
  readonly products: string;
  readonly reset: string;
  readonly resetViewport: string;
  readonly resizeFixtureNarrower: (fixtureType: PlanogramFixtureType, shelfNo: number) => string;
  readonly resizeFixtureNarrowerText: string;
  readonly resizeFixtureWider: (fixtureType: PlanogramFixtureType, shelfNo: number) => string;
  readonly resizeFixtureWiderText: string;
  readonly resizeShelfNarrower: (shelfNo: number) => string;
  readonly resizeShelfNarrowerText: string;
  readonly resizeShelfWider: (shelfNo: number) => string;
  readonly resizeShelfWiderText: string;
  readonly selectContainer: (label: string) => string;
  readonly selectFixture: (fixtureType: PlanogramFixtureType, shelfNo: number) => string;
  readonly selectPegboard: (id: string) => string;
  readonly selectPlacement: (label: string) => string;
  readonly selectProduct: (label: string) => string;
  readonly selectShelf: (shelfNo: number) => string;
  readonly shelf: (shelfNo: number) => string;
  readonly shelves: string;
  readonly viewportControls: string;
  readonly zoomIn: string;
  readonly zoomOut: string;
}

/**
 * A translucent placeholder rect in world coordinates, drawn while an
 * external (panel) drag hovers the stage — the canvas equivalent of the DOM
 * Dropper's placeholder element.
 */
export type PlanogramDropPreview = PlanogramWorldRect;

export interface KonvaPlanogramProps {
  assetProvider?: PlanogramAssetProvider;
  /** Insertion-point ghost for an active external drag; null hides it. */
  dropPreview?: PlanogramDropPreview | null;
  labels: PlanogramRendererLabels;
  /** External presentation channels; they never affect POG or base hit testing. */
  overlayChannels?: readonly OverlayChannelInput[];
  /**
   * Short labels pinned to entities (entity id → text), such as numbered
   * violation badges. Their own presentation dimension: a badge never grants
   * emphasis, and no channel can erase one.
   */
  overlayBadges?: ReadonlyMap<string, string>;
  /** Activates an interactive overlay badge without selecting the entity beneath it. */
  onOverlayBadgeActivate?: (request: PlanogramOverlayBadgeRequest) => void;
  onActivate?: (entity: PlanogramEntityRef, selection?: PlanogramSelection) => void;
  /**
   * Fires once every product/placement image request for the current scene
   * has settled (or immediately when image loading is disabled) — the signal
   * rasterization hosts wait for before calling `stage.toDataURL()`.
   */
  onAssetsSettled?: () => void;
  onContextRequest?: (request: PlanogramContextRequest) => void;
  onCommit?: (commit: PlanogramMoveCommit) => void;
  /** Fires with the hovered entity, and with null when the pointer leaves it or the stage. */
  onHoverChange?: (entity: PlanogramEntityRef | null) => void;
  onMoveResult?: (event: PlanogramMoveResultEvent) => void;
  onSelectionChange?: (selection: PlanogramSelection) => void;
  onViewportChange?: (viewport: ViewportState) => void;
  policy?: PlanogramPolicy;
  pog: PlanogramSnapshot;
  /**
   * Pans the viewport to center the entity when the id changes — the canvas
   * equivalent of the DOM renderer's scrollIntoView on the searched item.
   */
  scrollToEntityId?: string | null;
  selection?: PlanogramSelection;
  /** Konva stage handle, for rasterization hosts (`stage.toDataURL()`). */
  stageRef?: Ref<KonvaStage>;
  viewport?: ViewportState;
  viewportConfig?: ViewportConfig;
  view?: PlanogramViewConfig;
}
